/**
 * Pure (React-free) debouncer used by `useDebouncedValue`. Splits out the
 * timer logic so it can be unit-tested with bun:test fake timers without
 * needing a React renderer.
 */

export interface Debouncer<T> {
  /** Schedule (or reschedule) emission of `value` after `delayMs`. */
  push(value: T): void;
  /** Cancel any pending emission. Idempotent. */
  cancel(): void;
}

export function createDebouncer<T>(emit: (value: T) => void, delayMs: number): Debouncer<T> {
  let timer: ReturnType<typeof setTimeout> | null = null;

  return {
    push(value) {
      if (timer !== null) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        emit(value);
      }, delayMs);
    },
    cancel() {
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
    },
  };
}

/**
 * Helper that resolves search-input value to its "effective" form for fetching:
 *   - undefined when below the minimum length (skip server round-trip)
 *   - the trimmed value otherwise
 *
 * Mirrors the server-side `CompanyListQuerySchema` minimum (≥2 chars) so the
 * BFF/Core wouldn't reject it.
 */
export const SEARCH_MIN_CHARS = 2;

export function effectiveSearch(raw: string): string | undefined {
  const trimmed = raw.trim();
  return trimmed.length >= SEARCH_MIN_CHARS ? trimmed : undefined;
}
